import React, { useEffect, useState, useCallback, useRef } from "react";
import logoCall from "../../assets/images/logo_call_1.svg";
import micOff from "../../assets/images/mic-off.svg";
import micOn from "../../assets/images/mic-on.svg";
// import speakerOff from "../../assets/images/speaker_off.svg";
// import speakerOn from "../../assets/images/speaker_on.svg";
import callIcon from "../../assets/images/call-action.svg";
import { leaveChannel, setLocalAudioMuted } from "../../service/agora/agoraClient";
import { makeCall, cancelCall } from "../../service/callService";
import { ringtonePlayer } from "../../service/ringtonePlayer";

interface CallScreenProps {
  corlmiNumber: string;
  changeState: (state: string, error?: string) => void;
}

const CallScreen: React.FC<CallScreenProps> = ({ corlmiNumber, changeState }) => {
  const [callState, setCallState] = useState<string>("Calling");
  const [callDuration, setCallDuration] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  // const [isSpeakerOn, setIsSpeakerOn] = useState<boolean>(true);
  const callInitiated = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const ringingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const channelRef = useRef<string | null>(null);

  const cleanUpListener = useCallback(() => {
    if (unsubscribeRef.current) {
      console.log("[CallScreen] Cleaning up Firestore call listener");
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  const handleMuteToggle = async () => {
    const nextMuted = !isMuted;
    console.log(`[CallScreen] handleMuteToggle clicked. nextMuted=${nextMuted}`);
    setIsMuted(nextMuted);
    try {
      await setLocalAudioMuted(nextMuted);
    } catch (err) {
      console.error("Failed to toggle local audio mute:", err);
    }
  };

  /*
  const handleSpeakerToggle = async () => {
    const nextSpeaker = !isSpeakerOn;
    console.log(`[CallScreen] handleSpeakerToggle clicked. nextSpeaker=${nextSpeaker}`);
    setIsSpeakerOn(nextSpeaker);
    try {
      await toggleSpeakerphone(nextSpeaker);
    } catch (err) {
      console.error("Failed to toggle speakerphone:", err);
    }
  };
  */

   const endOngoingCall = () => {
    if ((callState === "Calling" || callState === "Ringing" || callState === "Connecting") && channelRef.current) {
      cancelCall(channelRef.current).catch((err) =>
        console.error("Error canceling call:", err)
      );
    }
    endCall(undefined);
  };

  const endCall = useCallback(
    (error: string | undefined) => {
      callInitiated.current = false;
      cleanUpListener();
      if (initDelayTimeoutRef.current) {
        clearTimeout(initDelayTimeoutRef.current);
        initDelayTimeoutRef.current = null;
      }
      if (ringingTimeoutRef.current) {
        clearTimeout(ringingTimeoutRef.current);
        ringingTimeoutRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      leaveChannel();
      if (error === undefined) changeState("initial");
      else changeState("initial", error);
    },
    [changeState, cleanUpListener],
  );

  // Play and stop the synthetic ringtone based on the callState
  useEffect(() => {
    if (callState === "Ringing") {
      console.log("[CallScreen] Call state is Ringing, starting ringtone");
      ringtonePlayer.start();
    } else {
      console.log("[CallScreen] Call state is not Ringing, stopping ringtone");
      ringtonePlayer.stop();
    }
    return () => {
      ringtonePlayer.stop();
    };
  }, [callState]);

  useEffect(() => {
    if (callState === "Calling" && !callInitiated.current && corlmiNumber) {
      const onActive = () => {
        console.log("[CallScreen] Receiver active / answered call. Transitioning to Connecting and stopping ringtone.");
        if (ringingTimeoutRef.current) {
          clearTimeout(ringingTimeoutRef.current);
          ringingTimeoutRef.current = null;
        }
        setCallState((prev) => (prev === "Connected" ? prev : "Connecting"));
      };

      const onConnected = () => {
        if (ringingTimeoutRef.current) {
          clearTimeout(ringingTimeoutRef.current);
          ringingTimeoutRef.current = null;
        }
        cleanUpListener();
        setCallState("Connected");
        setCallDuration(0);
        if (!timerRef.current) {
          timerRef.current = setInterval(() => {
            setCallDuration((prev) => prev + 1);
          }, 1000);
        }
      };

      callInitiated.current = true;

      // Add a 30-second delay before starting the call to allow local UI inspection
      // when backend connection is unavailable or errors out immediately.
      // Uncomment the delay wrapping code below when testing UI changes locally.
      /*
      initDelayTimeoutRef.current = setTimeout(() => {
      */
        makeCall({
          corlmiNumber: corlmiNumber.replace(/-/g, ""),
          endCall,
          onConnected,
          onActive,
        })
          .then((response) => {
            console.log(
              `Call initiated to voice channel: ${response.channel}, signaling: ${response.signalingChannel}`
            );
            channelRef.current = response.signalingChannel || response.channel;
            if (response.unsubscribe) {
              unsubscribeRef.current = response.unsubscribe;
            }
            setCallState("Ringing");

            // Start 3-minute timeout for no-answer
            ringingTimeoutRef.current = setTimeout(() => {
              console.log("[CallScreen] Call not picked up after 3 minutes, disconnecting.");
              if (channelRef.current) {
                cancelCall(channelRef.current).catch((err) =>
                  console.error("Error canceling call:", err)
                );
              }
              endCall("No answer");
            }, 180000);
          })
          .catch((error) => {
            console.log("Error making call", JSON.stringify(error));
            endCall(error?.message || "Error making call");
          });
      /*
      }, 30000);
      */
    }
  }, [corlmiNumber, callState, endCall, cleanUpListener]);

  useEffect(() => {
    return () => {
      cleanUpListener();
      if (initDelayTimeoutRef.current) {
        clearTimeout(initDelayTimeoutRef.current);
        initDelayTimeoutRef.current = null;
      }
      if (ringingTimeoutRef.current) {
        clearTimeout(ringingTimeoutRef.current);
        ringingTimeoutRef.current = null;
      }
    };
  }, [cleanUpListener]);

  return (
    <div className="bg-white flex flex-col justify-center items-center">
      <div>
        <img
          className="mb-[-100px] opacity-90 mix-blend-luminosity w-[267.89px] h-[276.86px]"
          alt="callButton"
          src={logoCall}
        />
        <div className="pb-10 w-[267.89px] flex-col justify-center items-center gap-2 inline-flex">
          <div className="text-[#344054] text-4xl font-semibold font-['Inter'] justify-center items-center leading-[5px]">
            {corlmiNumber}
          </div>
          <div className="mt-5 text-[#344054] text-2xl font-semibold font-['Inter'] justify-center items-center leading-relaxed">
            {callState === "Connected"
              ? `${Math.floor(callDuration / 60)}:${(callDuration % 60).toString().padStart(2, "0")}`
              : `${callState}...`}
          </div>
        </div>
      </div>
      
      {/* Center mute button, positioned above call end button */}
      <div className="mb-16 relative z-10 flex justify-center">
        <button
          onClick={handleMuteToggle}
          className="flex-col justify-start items-center gap-2 inline-flex hover:opacity-80 active:scale-95 transition-all"
        >
          <img className="w-8 h-8 relative overflow-hidden" src={isMuted ? micOff : micOn} alt="mute" />
          <div className={`text-[#1d2639] text-sm font-normal font-['Inter'] leading-tight ${isMuted ? "opacity-60" : ""}`}>
            {isMuted ? "Unmute" : "Mute"}
          </div>
        </button>
      </div>

      <div className="justify-center items-center gap-20 md:gap-42 inline-flex relative z-10">
        <img
          className="w-[72px] h-[72px] pl-[19.20px] pr-[18.86px] pt-[19.08px] pb-[18.98px] rounded-[100px] justify-center items-center cursor-pointer hover:scale-105 active:scale-95 transition-all"
          alt="endCall"
          role="button"
          src={callIcon}
          onClick={endOngoingCall}
        />
        {/* Commented out speaker button for now
        <button
          onClick={handleSpeakerToggle}
          className="flex-col justify-start items-center gap-2 inline-flex hover:opacity-80 active:scale-95 transition-all"
        >
          <img className="w-8 h-8 relative overflow-hidden" src={isSpeakerOn ? speakerOn : speakerOff} alt="speaker" />
          <div className={`text-[#1d2639] text-sm font-normal font-['Inter'] leading-tight ${!isSpeakerOn ? "opacity-60" : ""}`}>
            Speaker
          </div>
        </button>
        */}
      </div>
    </div>
  );
};

export default CallScreen;
